import type { PostFields } from '@buffer-mono/reminders-config'

import type {
  Gif,
  Image,
  Link,
  Retweet,
  SourceLink,
  Video,
} from '~publish/legacy/composer/composer/entities/factories'
import type { Document } from '~publish/legacy/composer/composer/entities/Document'
import type { ChannelData } from '~publish/legacy/composer/composer/entities/Draft'

/**
 * A single ordered attachment on the omni draft. One discriminated variant at
 * a time; mixed kinds may coexist in the same array. Wraps the legacy media
 * shapes so bridge mirroring is lossless; the GraphQL AssetInput @oneOf union
 * has no gif variant, so gif collapses to image at the wire-mapping boundary.
 */
export type OmniAsset =
  | { kind: 'image'; image: Image }
  | { kind: 'video'; video: Video }
  | { kind: 'gif'; gif: Gif }
  | { kind: 'document'; document: Document }
  | { kind: 'link'; link: Link }

/**
 * Channel-agnostic authored content — the channel-less draft. Mirrors the
 * `DraftContent` GraphQL shape (text, assets, aiAssisted): the content a user
 * writes before any channel is selected. Per-channel customisation stays in
 * legacy Flux drafts. Must stay plain serialisable data: no editor state, no
 * class instances, no derived values (character counts, validation).
 *
 * `urls`/`unshortenedUrls` are client-internal only (link preview, char count);
 * they are not part of the `DraftContent` wire shape — URLs live inline in
 * `text`. `draftId` maps to the persisted `DraftContent`/`ContentItem` id.
 * `title` maps to `ContentItem.title` — a separate field from the draft
 * content itself, unrelated to `ChannelVariant.title` (a per-network field).
 */
export interface OmniDraft {
  id: 'omni'
  text: string
  urls: string[]
  unshortenedUrls: string[]
  assets: OmniAsset[]
  aiAssisted: boolean
  draftId: string | null
  title: string | null
}

export function createEmptyOmniDraft(): OmniDraft {
  return {
    id: 'omni',
    text: '',
    urls: [],
    unshortenedUrls: [],
    assets: [],
    aiAssisted: false,
    draftId: null,
    title: null,
  }
}

/**
 * One committed thread post — the normalized counterpart of legacy's
 * `Draft.thread: Post[] | null` (`Post` in
 * `~publish/legacy/composer/composer/entities/Draft/Draft.ts`). There is no
 * `enabledAttachmentType`: the per-item link-wins gate is resolved at
 * projection time, so `assets` already encodes the outcome (a link asset when
 * the gate won, media otherwise) instead of carrying a second slot to keep in
 * sync. An item is a committed snapshot; the active item's live content lives
 * on the variant's flat content fields, not here (see `ChannelVariant`).
 */
export interface ThreadItem {
  text: string
  urls: string[]
  unshortenedUrls: string[]
  assets: OmniAsset[]
}

/**
 * One channel's authored content, keyed by `channelId` in
 * `PostState.channelVariants`. The per-channel counterpart of `OmniDraft`: the
 * same content fields plus the per-channel metadata scalars (`updateType`,
 * `isReminder`, `title`, `enabledAttachmentType`), the per-service settings
 * bags (`channelData`, `sourceLink`, `retweet`, `selectedStickers`), and
 * thread structure (`threadItems`), customised per network. `places` and
 * `document` stay off the variant on purpose: `places` has no UI caller left
 * (dead field), and `document` already flows through `assets` as a completed
 * `OmniAsset`. Must stay plain serialisable data — same constraints as
 * `OmniDraft`.
 *
 * `threadItems` is normalized relative to legacy's `Draft.thread: Post[] |
 * null`: there is no null case, and "is a thread" is `threadItems.length > 1`
 * — computed by consumers, never stored (`[]` and a single-item array both
 * mean "not a thread"). `threadItems[activeIndex]` is committed-stale between
 * index switches, same as legacy `thread[activeIndex]`; the variant's flat
 * content fields above are the live active item.
 */
export interface ChannelVariant {
  channelId: string
  text: string
  urls: string[]
  unshortenedUrls: string[]
  assets: OmniAsset[]
  aiAssisted: boolean
  updateType: string | null
  isReminder: boolean
  title: string | null
  enabledAttachmentType: string | null
  channelData: ChannelData | null
  sourceLink: SourceLink | null
  retweet: Retweet | null
  selectedStickers: PostFields[]
  threadItems: ThreadItem[]
}

/** The mutable content fields of a `ChannelVariant` (everything but its key). */
export type ChannelVariantContent = Omit<ChannelVariant, 'channelId'>

export function createEmptyChannelVariant(channelId: string): ChannelVariant {
  return {
    channelId,
    text: '',
    urls: [],
    unshortenedUrls: [],
    assets: [],
    aiAssisted: false,
    updateType: null,
    isReminder: false,
    title: null,
    enabledAttachmentType: null,
    channelData: null,
    sourceLink: null,
    retweet: null,
    selectedStickers: [],
    threadItems: [],
  }
}
